import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, Package, ShieldCheck, Truck } from 'lucide-react';
import { LoginForm } from '../components/auth/LoginForm';
import { useIsAuthenticated } from '../store/useAuthStore';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [searchParams] = useSearchParams();
  const [showTestCreds, setShowTestCreds] = useState(false);
  const { branding, isLoading } = useTenantBranding();

  const successMessage = searchParams.get('message');
  const logoutReason = searchParams.get('reason');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLoginSuccess = () => {
    navigate('/dashboard', { replace: true });
  };

  // Only show test credentials in development
  const testCredentials = import.meta.env.MODE === 'development' ? [
    {
      role: 'Admin',
      email: 'admin@bloom.local',
      password: 'admin123',
      description: 'Full system access',
      color: 'bg-rose-50 border-rose-200 text-rose-800'
    },
    {
      role: 'Supervisor',
      email: 'supervisor@bloom.local',
      password: 'supervisor123',
      description: 'Manages account managers',
      color: 'bg-orange-50 border-orange-200 text-orange-800'
    },
    {
      role: 'Distributor',
      email: 'distributor@bloom.local',
      password: 'distributor123',
      description: 'Distributor dashboard',
      color: 'bg-sky-50 border-sky-200 text-sky-800'
    },
    {
      role: 'Physician (Verified)',
      email: 'physician@bloom.local',
      password: 'physician123',
      description: 'Verified physician account',
      color: 'bg-emerald-50 border-emerald-200 text-emerald-800'
    },
    {
      role: 'Physician (ID Proofing)',
      email: 'idproofing.physician@bloom.local',
      password: 'idproofing123',
      description: 'Test ID verification flow',
      color: 'bg-amber-50 border-amber-200 text-amber-800'
    },
    {
      role: 'Physician (Pending)',
      email: 'pending.physician@bloom.local',
      password: 'pending123',
      description: 'Awaiting approval',
      color: 'bg-stone-50 border-stone-200 text-stone-700'
    }
  ] : [];

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  /**
   * Fill a controlled input the way React will actually notice.
   *
   * React installs its own `value` setter on the input prototype and tracks the
   * last value it wrote. Assigning `input.value = x` goes through that setter,
   * so React concludes nothing changed and the subsequent `input` event is
   * ignored — the DOM shows the text while form state stays empty. Calling the
   * NATIVE setter bypasses the tracker, so the dispatched event is believed.
   *
   * Without this, Quick Login filled both boxes and then failed validation with
   * "Password must be at least 6 characters" under eight visible dots.
   */
  const setReactInputValue = (input: HTMLInputElement, value: string) => {
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    setter?.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const quickLogin = (email: string, password: string) => {
    const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
    const passwordInput = document.querySelector('input[type="password"]') as HTMLInputElement;
    const submitButton = document.querySelector('button[type="submit"]') as HTMLButtonElement;

    if (emailInput && passwordInput && submitButton) {
      setReactInputValue(emailInput, email);
      setReactInputValue(passwordInput, password);

      setTimeout(() => {
        submitButton.click();
      }, 100);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--neutral-50)' }}>
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 spinner-brand"></div>
          <p style={{ color: 'var(--neutral-600)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const showDevCreds = (import.meta.env.MODE === 'development' || import.meta.env.VITE_SHOW_TEST_CREDENTIALS === 'true') && testCredentials.length > 0;
  const company = branding.companyName;

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--neutral-50)' }}>
      {/* Brand panel (desktop) */}
      <aside
        className="relative hidden overflow-hidden lg:flex lg:w-[46%] xl:w-1/2"
        style={{ background: 'linear-gradient(155deg, var(--brand-primary-dark) 0%, var(--brand-primary) 60%, var(--brand-primary-light) 100%)' }}
      >
        <div aria-hidden className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-white/[0.07]" />
        <div aria-hidden className="absolute -bottom-48 -left-24 h-[420px] w-[420px] rounded-full bg-white/[0.05]" />

        <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3">
            {branding.logo && (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl p-2 shadow-sm" style={{ background: '#fff' }}>
                <img src={branding.logo} alt="" className="h-full w-full object-contain" />
              </div>
            )}
            <span className="text-lg font-semibold tracking-tight" style={{ color: '#fff' }}>{company}</span>
          </div>

          <div className="max-w-md">
            <h2 className="text-4xl font-semibold leading-tight xl:text-[2.75rem]" style={{ color: '#fff' }}>
              Ordering for your practice, made simple.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/80">
              Place orders, track every shipment and keep on top of invoices, all in one place.
            </p>
            <ul className="mt-10 space-y-4">
              {[
                { icon: Package, text: 'Order in minutes, for patients or for your clinic' },
                { icon: Truck, text: 'Follow every order from pharmacy to doorstep' },
                { icon: ShieldCheck, text: 'Encrypted and HIPAA compliant' }
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3 text-sm text-white/90">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                    <Icon className="h-4 w-4 text-white" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-sm text-white/60">© {new Date().getFullYear()} {company}</p>
        </div>
      </aside>

      {/* Sign-in */}
      <main className="flex flex-1 flex-col justify-start px-5 pb-10 pt-14 sm:justify-center sm:px-10 sm:py-10">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo (mobile) */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            {branding.logo && (
              <div
                className="flex h-10 w-10 items-center justify-center rounded-xl border p-1.5"
                style={{ background: '#fff', borderColor: 'var(--neutral-200)' }}
              >
                <img src={branding.logo} alt="" className="h-full w-full object-contain" />
              </div>
            )}
            <span className="text-base font-semibold" style={{ color: 'var(--neutral-900)' }}>{company}</span>
          </div>

          <h1 className="text-2xl font-semibold sm:text-3xl" style={{ color: 'var(--neutral-900)' }}>Sign in</h1>
          <p className="mt-1.5 text-sm" style={{ color: 'var(--neutral-600)' }}>Welcome back to {company}.</p>

          {successMessage && (
            <div
              role="status"
              className="mt-6 rounded-xl border px-4 py-3 text-sm font-medium"
              style={{ background: 'var(--success-light)', borderColor: 'var(--success)', color: 'var(--success-dark)' }}
            >
              {successMessage}
            </div>
          )}

          {logoutReason === 'inactivity' && (
            <div role="status" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-medium">You were signed out after a period of inactivity.</span> Sign in again to continue.
            </div>
          )}

          <LoginForm className="mt-8" autoFocus onSuccess={handleLoginSuccess} />

          <p className="mt-8 text-center text-sm" style={{ color: 'var(--neutral-600)' }}>
            New to {company}?{' '}
            <a href="/request-access" className="font-medium hover:underline" style={{ color: 'var(--brand-primary)' }}>
              Request access
            </a>
          </p>

          {showDevCreds && (
            <div className="mt-10 rounded-xl border border-dashed" style={{ borderColor: 'var(--neutral-300)' }}>
              <button
                type="button"
                onClick={() => setShowTestCreds(!showTestCreds)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-xs font-medium"
                style={{ color: 'var(--neutral-600)' }}
                aria-expanded={showTestCreds}
              >
                <span>Test accounts ({import.meta.env.MODE})</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showTestCreds ? 'rotate-180' : ''}`} />
              </button>
              {showTestCreds && (
                <ul className="divide-y border-t" style={{ borderColor: 'var(--neutral-200)' }}>
                  {testCredentials.map(cred => (
                    <li key={cred.email} className="flex items-center justify-between gap-3 px-4 py-2.5" style={{ borderColor: 'var(--neutral-200)' }}>
                      <div className="min-w-0">
                        <div className="text-xs font-medium" style={{ color: 'var(--neutral-800, var(--neutral-900))' }}>{cred.role}</div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(cred.email)}
                          title="Copy email"
                          className="truncate font-mono text-[11px] hover:underline"
                          style={{ color: 'var(--neutral-600)' }}
                        >
                          {cred.email}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => quickLogin(cred.email, cred.password)}
                        className="shrink-0 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-black/5"
                        style={{ borderColor: 'var(--neutral-200)', color: 'var(--neutral-700)' }}
                      >
                        Sign in
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
